import React, { useContext, useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { DataContext } from '../contexts/DataContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortUp, faSortDown, faFileCsv, faFileExcel, faChevronDown, faChevronUp, faTimes, faBox, faBank, faDollarSign } from '@fortawesome/free-solid-svg-icons';
import Papa from 'papaparse';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import MiniDashboard from './MiniDashboard';
import logo from '../wisdom.png';
import Pagination from '../utils/Pagination';
import ExportCSVButton from '../utils/ExportCSVButton';
import SearchForm from '../utils/SearchForm';

const calculateAging = (pedimentoFecha) => {
  const pedimentoDate = new Date(pedimentoFecha);
  const currentDate = new Date();
  const differenceInTime = currentDate.getTime() - pedimentoDate.getTime();
  return Math.floor(differenceInTime / (1000 * 3600 * 24)); // Convert to days
};

const SortIcon = ({ isSorted, ascending }) => {
  if (!isSorted) return null;
  return (
    <span className="inline-block ml-2">
      {ascending ? (
        <FontAwesomeIcon icon={faSortUp} className="text-gray-400" />
      ) : (
        <FontAwesomeIcon icon={faSortDown} className="text-gray-400" />
      )}
    </span>
  );
};

const FilterMenu = ({ onFilterChange, statusOptions }) => {
  const [selectedStatuses, setSelectedStatuses] = useState([]);

  const handleStatusChange = (selectedOptions) => {
    setSelectedStatuses(selectedOptions);
    onFilterChange(selectedOptions.map(option => option.value));
  };

  return (
    <div className="mb-4" style={{ width: '250px' }}> {/* Fixed width */}
      <Select
        isMulti
        options={statusOptions}
        value={selectedStatuses}
        onChange={handleStatusChange}
        placeholder="Select Status"
        classNamePrefix="select" // Optional for styling
      />
    </div>
  );
};

const ShipmentsTable = ({ shipments, onSortChange, sortField, sortAscending }) => {
  const createSortHandler = (field) => () => onSortChange(field);

  const [expandedRows, setExpandedRows] = useState({});

  const toggleRow = (key) => {
    setExpandedRows(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isRowExpanded = (key) => {
    return !!expandedRows[key];
  };

  const renderAdditionalData = (shipment) => {
    if (!isRowExpanded(shipment.Trafico)) return null;

    const renderComprobadosTable = (comprobados) => {
      return (
        <table className="min-w-full mt-4 border border-gray-200">
          <thead className="border-b bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Concepto</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Factura Proveedor</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Proveedor</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Saldo</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {comprobados.map((comp, index) => (
              <tr key={index}>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{comp.Concepto}</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{comp.FacturaProveedor}</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{comp.Proveedor}</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{comp.Saldo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    };

    // Render the comprobados table
    const comprobadosTable = shipment.comprobados && shipment.comprobados.length > 0
      ? renderComprobadosTable(shipment.comprobados)
      : <div className="text-sm text-gray-500 p-2">No comprobados data available.</div>;



    const formatCurrency = (value) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        // You can adjust the following options to suit your formatting needs
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    };

    // Determine if TotalFinanciado is negative, then apply red background
    const isNegative = (shipment.TotalFinanciado * -1) > 0;
    const additionalDataRowClass = isNegative ? "bg-red-100" : "bg-white-100";

    const renderContainerData = (containers) => {
      return containers.map(container => container.container_number.trim()).join(', ');
    };

    let containerOrCajaDisplay;
    if (shipment.Aduana === '160' && shipment.container_data && shipment.container_data.length > 0) {
      containerOrCajaDisplay = <div className="col-span-1">{renderContainerData(shipment.container_data)}</div>;
    } else {
      containerOrCajaDisplay = <div className="col-span-1">{shipment.Caja}</div>;
    }

    return (
      <tr className={additionalDataRowClass}>
        <td colSpan="12">
          <div className="p-2">
            <div className="grid grid-cols-6 gap-4 items-center">
              <div className="flex items-center col-span-1">
                <FontAwesomeIcon icon={faBox} className="mr-2" />
                <span className="font-semibold">CantidadCajas:</span>
              </div>
              <div className="col-span-1">{shipment.CantidadCajas}</div>

              <div className="flex items-center col-span-1">
                <span className="font-semibold">Caja:</span>
              </div>
              {containerOrCajaDisplay}

              <div className="flex items-center col-span-1">
                <FontAwesomeIcon icon={faBank} className="mr-2" />
                <span className="font-semibold">BancoPagoPedimento:</span>
              </div>
              <div className="col-span-1">{shipment.BancoPagoPedimento}</div>

              <div className="flex items-center col-span-1">
                <FontAwesomeIcon icon={faDollarSign} className="mr-2" />
                <span className="font-semibold">TotalCompro:</span>
              </div>
              <div className="col-span-1">{formatCurrency(shipment.TotalCompro)}</div>

              <div className="flex items-center col-span-1">
                <span className="font-semibold">TotalAnticipo:</span>
              </div>
              <div className="col-span-1">{formatCurrency(shipment.TotalAnticipo)}</div>

              <div className="flex items-center col-span-1">
                <span className="font-semibold">TotalFinanciado:</span>
              </div>
              <div className="col-span-1">{formatCurrency(shipment.TotalFinanciado)}</div>
            </div>
            {comprobadosTable}

            <button
              onClick={() => toggleRow(shipment.Trafico)}
              className="text-gray-500 hover:text-gray-700 mt-2 p-1 rounded-full"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </td>
      </tr>
    );
  };


  const getAgingStyle = (days) => {
    if (days > 20) {
      return "bg-red-200 text-red-800"; // red background for more than 20 days
    } else if (days >= 10) {
      return "bg-yellow-200 text-yellow-800"; // yellow background for 10-20 days
    } else {
      return "bg-green-200 text-green-800"; // green background for 0-9 days
    }
  };

  const getRojoVerdeStyle = (value) => {
    switch (value) {
      case 'V':
        return "bg-green-600 text-green-200"; // Green background and text for 'V'
      case 'R':
        return "bg-red-600 text-red-200"; // Red background and text for 'R'
      default:
        return ""; // Default style (can be adjusted as needed)
    }
  };


  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead className="border-b-2 border-gray-200 bg-gray-50">
          <tr>
            <th
              className="w-1/6 py-3 px-4 uppercase font-semibold text-sm cursor-pointer text-center"
              onClick={createSortHandler('Trafico')} // Use the appropriate field value here
            >              Trafico
              <SortIcon isSorted={sortField === 'Trafico'} ascending={sortAscending} />
            </th>
            <th className="w-1/6 py-3 px-4 uppercase font-semibold text-sm cursor-pointer text-center" onClick={createSortHandler('Aduana')}>
              Aduana
              <SortIcon isSorted={sortField === 'Aduana'} ascending={sortAscending} />
            </th>
            <th className="w-1/6 py-3 px-4 uppercase font-semibold text-sm cursor-pointer text-center" onClick={createSortHandler('Patente')}>
              Patente
              <SortIcon isSorted={sortField === 'Patente'} ascending={sortAscending} />
            </th>
            <th className="w-1/6 py-3 px-4 uppercase font-semibold text-sm cursor-pointer text-center" onClick={createSortHandler('Pedimento')}>
              Pedimento
              <SortIcon isSorted={sortField === 'Pedimento'} ascending={sortAscending} />
            </th>
            <th className="w-1/6 py-3 px-4 uppercase font-semibold text-sm cursor-pointer text-center" onClick={createSortHandler('Regimen')}>
              Regimen
              <SortIcon isSorted={sortField === 'Regimen'} ascending={sortAscending} />
            </th>
            <th className="w-1/6 py-3 px-4 uppercase font-semibold text-sm cursor-pointer text-center" onClick={createSortHandler('RojoVerde')}>
              RV
              <SortIcon isSorted={sortField === 'RojeVerde'} ascending={sortAscending} />
            </th>
            <th className="w-1/6 py-3 px-4 uppercase font-semibold text-sm cursor-pointer text-center" onClick={createSortHandler('ClienteNombre')}>
              Cliente
              <SortIcon isSorted={sortField === 'ClienteNombre'} ascending={sortAscending} />
            </th>
            <th className="w-1/6 py-3 px-4 uppercase font-semibold text-sm cursor-pointer text-center" onClick={createSortHandler('FacturistaNombre')}>
              Facturista
              <SortIcon isSorted={sortField === 'FacturistaNombre'} ascending={sortAscending} />
            </th>
            <th className="w-1/6 py-3 px-4 uppercase font-semibold text-sm cursor-pointer text-center" onClick={createSortHandler('Impuestos')}>
              Impuestos
              <SortIcon isSorted={sortField === 'Impuestos'} ascending={sortAscending} />
            </th>
            <th className="w-1/6 py-3 px-4 uppercase font-semibold text-sm cursor-pointer text-center" onClick={createSortHandler('Estatus')}>
              Estatus
              <SortIcon isSorted={sortField === 'Estatus'} ascending={sortAscending} />
            </th>
            <th className="w-1/6 py-3 px-4 uppercase font-semibold text-sm cursor-pointer text-center" onClick={createSortHandler('PedimentoFecha')}>
              Pedimento Fecha
              <SortIcon isSorted={sortField === 'PedimentoFecha'} ascending={sortAscending} />
            </th>
            <th className="py-3 px-4 uppercase font-semibold text-sm cursor-pointer text-center" onClick={() => createSortHandler('aging')()}>
              Antiguedad
              <SortIcon isSorted={sortField === 'aging'} ascending={sortAscending} />
            </th>
            <th className="py-3 px-4 uppercase font-semibold text-sm cursor-pointer text-center">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {shipments.map((shipment) => {
            const agingDays = calculateAging(shipment.PedimentoFecha);
            return (
              <>
                <tr key={shipment.Trafico} className="text-sm border-t border-gray-200 hover:bg-gray-100">
                  <td className="py-3 px-4 text-center">{shipment.Trafico}</td>
                  <td className="py-3 px-4 text-center">
                    <span className="inline-block bg-blue-100 text-blue-800 rounded-full px-3 py-1 text-xs font-semibold" style={{ width: '50px' }}>
                      {shipment.Aduana}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">{shipment.Patente}</td>
                  <td className="py-3 px-4 text-center">{shipment.Pedimento}</td>
                  <td className="py-3 px-4 text-center">{shipment.Regimen}</td>
                  <td className="py-3 px-4 text-center">
                    <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${getRojoVerdeStyle(shipment.RojoVerde)}`}>
                      {shipment.RojoVerde}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">{shipment.ClienteNombre}</td>
                  <td className="py-3 px-4 text-center">{shipment.FacturistaNombre}</td>
                  <td className="py-3 px-4 text-center">{shipment.Impuestos}</td>
                  <td className="py-3 px-4 text-center">{shipment.Estatus}</td>
                  <td className="py-3 px-4 text-center">{shipment.PedimentoFecha}</td>
                  <td className="py-3 px-4 text-center">
                    <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${getAgingStyle(agingDays)}`}>
                      {agingDays}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <button
                      onClick={() => toggleRow(shipment.Trafico)}
                      className="rounded bg-blue-500 p-2 text-white"
                    >
                      <FontAwesomeIcon icon={isRowExpanded(shipment.Trafico) ? faChevronUp : faChevronDown} />
                    </button>
                  </td>
                </tr>
                {renderAdditionalData(shipment)}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const ShipmentsComponent = () => {
  const { pendingBillingData, isLoading, fetchPendingBillingData } = useContext(DataContext);
  const [filteredShipments, setFilteredShipments] = useState([]);

  const [sortField, setSortField] = useState(null);
  const [sortAscending, setSortAscending] = useState(true);

  const [pageSize, setPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedCustomers, setSelectedCustomers] = useState([]); // For multiple search
  const [shipmentStatus, setShipmentStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState(''); // Define searchTerm , text box

  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedAduanas, setSelectedAduanas] = useState([]);
  const [aduanaOptions, setAduanaOptions] = useState([]);

  useEffect(() => {
    fetchPendingBillingData();
  }, [fetchPendingBillingData]);

  useEffect(() => {
    if (pendingBillingData) {
      // Extract unique statuses
      const uniqueStatuses = Array.from(new Set(pendingBillingData.map(item => item.Estatus)))
        .filter(status => status)
        .map(status => ({ value: status, label: status }));
      setStatusOptions(uniqueStatuses);

      // Extract unique aduanas
      const uniqueAduanas = Array.from(new Set(pendingBillingData.map(item => item.Aduana)))
        .filter(aduana => aduana)
        .map(aduana => ({ value: aduana, label: aduana }));
      setAduanaOptions(uniqueAduanas);
    }
  }, [pendingBillingData]);

  const handleAduanaChange = selectedOptions => {
    setSelectedAduanas(selectedOptions || []);
    filterData(selectedOptions.map(option => option.value));
  };


  // Get unique ClienteNombre for my combo box
  const customerOptions = pendingBillingData
    ? Array.from(new Set(pendingBillingData.map(data => data.ClienteNombre)))
      .map(customerName => ({ value: customerName, label: customerName }))
    : [];

  const handleCustomerChange = selectedOptions => {
    setSelectedCustomers(selectedOptions || []);
  };


  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const paginatedShipments = filteredShipments.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const sortData = (field, ascending) => {
    const sortedData = [...filteredShipments].sort((a, b) => {
      let valA, valB;

      if (field === 'aging') {
        valA = calculateAging(a.PedimentoFecha);
        valB = calculateAging(b.PedimentoFecha);
      } else {
        valA = a[field];
        valB = b[field];
        if (field === 'PedimentoFecha') {
          valA = new Date(valA);
          valB = new Date(valB);
        }
      }

      if (valA < valB) {
        return ascending ? -1 : 1;
      }
      if (valA > valB) {
        return ascending ? 1 : -1;
      }
      return 0;
    });
    setFilteredShipments(sortedData);
  };


  const handleSortChange = (field) => {
    const isAscending = (field === sortField) ? !sortAscending : true;
    setSortField(field);
    setSortAscending(isAscending);
    sortData(field, isAscending);
  };

  const handleSearch = (term) => {
    setSearchTerm(term); // Update state when search term changes
    filterData();
  };

  const handleFilterChange = (filterValue) => {
    setShipmentStatus(filterValue); // Update state when filter changes
    filterData();
  };

  const filterData = () => {
    let filtered = pendingBillingData || [];

    // Filter by customers
    if (selectedCustomers.length) {
      filtered = filtered.filter(shipment =>
        selectedCustomers.some(selected => selected.value === shipment.ClienteNombre)
      );
    }

    // Filter by aduanas
    if (selectedAduanas.length) {
      filtered = filtered.filter(shipment =>
        selectedAduanas.some(selected => selected.value === shipment.Aduana)
      );
    }

    // Filter by statuses
    if (shipmentStatus.length) {
      filtered = filtered.filter(shipment =>
        shipmentStatus.includes(shipment.Estatus)
      );
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(shipment =>
        Object.values(shipment).some(value =>
          value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    setFilteredShipments(filtered);
  };

  useEffect(filterData, [selectedCustomers, shipmentStatus, searchTerm, pendingBillingData, selectedAduanas]);


  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <FontAwesomeIcon icon={faSpinner} className="text-xl font-semibold animate-spin" />
        <div className="text-xl font-semibold ml-2">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <nav className="w-full flex justify-between items-center px-6 py-4 bg-white shadow">
        <div className="flex items-center">
          <img src={logo} alt="Wisdom logo" className="h-12 mr-2" />
        </div>
        <div className="text-xl font-semibold text-gray-700">
          Traffic Monitor
        </div>
      </nav>
      <div className="container mx-auto p-6 bg-gray-100">


        <div className="flex justify-between mb-4">
          <div style={{ width: '250px' }}>
            <Select
              options={customerOptions}
              isMulti
              onChange={handleCustomerChange}
              className="mb-4"
              placeholder="Select Customers"
              classNamePrefix="select"
            />
          </div>
          <div style={{ width: '250px' }}> {/* Aduana combo box */}
            <Select
              options={aduanaOptions}
              isMulti
              onChange={handleAduanaChange}
              className="mbf-4"
              placeholder="Select Aduanas"
              classNamePrefix="select"
            />
          </div>
          <FilterMenu onFilterChange={handleFilterChange} statusOptions={statusOptions} />
          <div className="flex items-center gap-4">
            <ExportCSVButton data={filteredShipments} filename="shipments.csv" />
          </div>
        </div>
        <SearchForm onSearch={handleSearch} />
        <MiniDashboard shipments={filteredShipments} calculateAging={calculateAging} />
        <Pagination
          total={filteredShipments.length}
          pageSize={pageSize}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onPageSizeChange={setPageSize}
        />
        <div className="bg-white p-4 shadow-md rounded-lg">
          <ShipmentsTable
            shipments={paginatedShipments}
            onSortChange={handleSortChange}
            sortField={sortField}
            sortAscending={sortAscending}
          />
        </div>
      </div>
    </>
  );
};

export default ShipmentsComponent;
